import { TSupportMessage } from '../../services/resources/supportChat/types'
import { useState } from 'react'
import { FiDownload, FiFile } from 'react-icons/fi'

const IMAGE_FORMATS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg', 'heic']

/**
 * Imagem é para ser vista, não baixada: se o anexo for imagem, a bolha mostra a
 * própria imagem. Qualquer outro formato vira um cartão de arquivo.
 */
const isImage = (url: string, format?: string | null) => {
  if (format && IMAGE_FORMATS.includes(format.toLowerCase().replace('.', ''))) return true

  const extension = url.split('?')[0].split('.').pop()?.toLowerCase()

  return !!extension && IMAGE_FORMATS.includes(extension)
}

const fileNameFromUrl = (url: string) => decodeURIComponent(url.split('?')[0].split('/').pop() ?? 'arquivo')

const formatSize = (bytes?: number | null) => {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const Attachment = ({
  url,
  format,
  sizeBytes,
  onOpenImage
}: {
  url: string
  format?: string | null
  sizeBytes?: number | null
  onOpenImage: (url: string) => void
}) => {
  if (isImage(url, format)) {
    return (
      <button
        type="button"
        onClick={() => onOpenImage(url)}
        className="block overflow-hidden rounded-lg border-0 bg-transparent p-0">
        <img
          src={url}
          alt="Anexo"
          loading="lazy"
          className="max-h-60 w-auto max-w-full cursor-zoom-in rounded-lg object-contain"
        />
      </button>
    )
  }

  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 hover:bg-gray-50">
      <FiFile className="shrink-0 text-gray-400" />
      <span className="max-w-[180px] truncate">{fileNameFromUrl(url)}</span>
      {sizeBytes ? <span className="text-gray-400">{formatSize(sizeBytes)}</span> : null}
      <FiDownload className="shrink-0 text-gray-400" />
    </a>
  )
}

type MessageBubbleProps = {
  message: TSupportMessage
  /** `true` quando a mensagem é do próprio paciente (fica à direita). */
  isOwn: boolean
  /**
   * `false` quando a mensagem continua um bloco do mesmo remetente — aí o nome
   * não se repete e a bolha fica colada na anterior, como em qualquer chat.
   */
  showSender?: boolean
}

const MessageBubble = ({ message, isOwn, showSender = true }: MessageBubbleProps) => {
  const [zoomedImage, setZoomedImage] = useState<string | null>(null)

  const attachments = Object.values(message.attachments ?? {})
  const isBot = message.sender_type === 'bot'

  const bubbleStyle = (() => {
    if (isOwn) return 'bg-green-700 text-white rounded-br-sm'
    if (isBot) return 'bg-violet-50 text-gray-800 border border-violet-100 rounded-bl-sm'

    return 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
  })()

  const time = new Date(message.created_at).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit'
  })

  return (
    <>
      <div
        className={`flex w-full flex-col gap-1 ${isOwn ? 'items-end' : 'items-start'} ${
          showSender ? 'mt-3 first:mt-0' : ''
        }`}>
        {!isOwn && showSender && (
          <span className="px-1 text-[11px] font-semibold text-gray-500">
            {isBot ? 'Bia (assistente)' : (message.sender_name ?? 'Atendente')}
          </span>
        )}

        <div className={`max-w-[75%] rounded-2xl px-4 py-2 shadow-sm ${bubbleStyle}`}>
          {message.content && <p className="whitespace-pre-wrap break-words text-sm">{message.content}</p>}

          {attachments.length > 0 && (
            <div className="mt-2 flex flex-col gap-2">
              {attachments.map((attachment) => (
                <Attachment
                  key={attachment.url}
                  url={attachment.url}
                  format={attachment.format}
                  sizeBytes={attachment.size_bytes}
                  onOpenImage={setZoomedImage}
                />
              ))}
            </div>
          )}

          <span className={`mt-1 block text-right text-[10px] ${isOwn ? 'text-green-100' : 'text-gray-400'}`}>
            {time}
          </span>
        </div>
      </div>

      {/* Lightbox: imagem pequena na bolha, grande quando clica. */}
      {zoomedImage && (
        <button
          type="button"
          aria-label="Fechar imagem"
          onClick={() => setZoomedImage(null)}
          className="fixed inset-0 z-50 flex cursor-zoom-out items-center justify-center border-0 bg-black/80 p-8">
          <img src={zoomedImage} alt="Anexo ampliado" className="max-h-full max-w-full object-contain" />
        </button>
      )}
    </>
  )
}

export default MessageBubble
